import { createContext, useState, useMemo, useEffect } from "react";
import type { Dispatch, SetStateAction, ReactNode } from "react";
import { clientOnly$ } from "vite-env-only/macros";

interface TrackingContext {
  jobSeekerProfileId: string | null;
  setJobSeekerProfileId?: Dispatch<SetStateAction<string | null>>;
}

export const TrackingContext = createContext<TrackingContext>({
  jobSeekerProfileId: null,
} as TrackingContext);

const canUseSessionStorage = () => {
  try {
    sessionStorage.setItem("gh-test", "gh-test");
    sessionStorage.getItem("gh-test");
    return true;
  } catch (e) {
    return false;
  }
};

const retrieveFromStorage = clientOnly$((key: string) => {
  const storage = canUseSessionStorage() ? sessionStorage.getItem("gh-tracking") : null;
  if (storage) {
    try {
      const parsed = JSON.parse(storage);
      return parsed[key] || null;
    } catch (err) {
      return null;
    }
  }
});

export const TrackingContextProvider = ({ children }: { children: ReactNode }) => {
  const [jobSeekerProfileId, setJobSeekerProfileId] = useState(
    retrieveFromStorage ? retrieveFromStorage("profileId") : null
  );

  const [useStorage] = useState(canUseSessionStorage());

  const value = useMemo(() => {
    return {
      jobSeekerProfileId,
      setJobSeekerProfileId,
    };
  }, [jobSeekerProfileId, setJobSeekerProfileId]);

  useEffect(() => {
    if (useStorage && jobSeekerProfileId) {
      try {
        sessionStorage.setItem(
          "gh-tracking",
          JSON.stringify({
            profileId: jobSeekerProfileId,
          })
        );
      } catch {
        return;
      }
    }
  }, [useStorage, jobSeekerProfileId]);

  return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
};
